/**
 * Los logos de las monedas y de los DEX: los de verdad, los que el usuario ya ha visto en la
 * pantalla del propio DEX o en su wallet.
 *
 * SON FICHEROS DEL BUNDLE, NO IMAGENES DE UN CDN, y no es una manía: pedirle el logo a un tercero
 * en tiempo de ejecución le cuenta a ese tercero quién está mirando qué par, tarda lo que tarde él,
 * y se ve roto el día que ese dominio cambie de sitio. Los ficheros se bajan UNA VEZ con
 * `scripts/bajar-logos.mjs` —de la lista de tokens de Uniswap y PancakeSwap, de LI.FI y del CDN de
 * la propia Robinhood— y a partir de ahí son un asset más, servido desde nuestro dominio.
 *
 * Se cargan por CARPETA, no por una lista escrita a mano: dejar `SOL.png` en `assets/monedas/` es
 * todo lo que hace falta para que esa moneda tenga logo. El que no lo tenga cae en una insignia con
 * su inicial y los colores de su marca — nunca un hueco vacío.
 */

/** El nombre del fichero ES el símbolo. `import.meta.glob` los mete en el bundle en compilación. */
const rutas = (mapa: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(mapa).map(([ruta, url]) => [
      ruta.split("/").pop()!.replace(/\.[a-z0-9]+$/i, "").toUpperCase(),
      url as string,
    ])
  );

const LOGOS = rutas(
  import.meta.glob("../assets/monedas/*.{png,jpg,jpeg,svg,webp}", {
    eager: true, query: "?url", import: "default",
  })
);
const LOGOS_DEX = rutas(
  import.meta.glob("../assets/dex/*.{png,jpg,jpeg,svg,webp}", {
    eager: true, query: "?url", import: "default",
  })
);

/**
 * MONEDAS QUE SON LA MISMA MONEDA. `WETH` es ether envuelto y lleva el logo de ether; `UBTC` y
 * `BTCB` son bitcoin puenteado a HyperEVM y a BNB Chain. Sin esto, la misma moneda saldría con
 * logo en una red y con una letra en otra, que se lee como que una de las dos está mal.
 *
 * `USDT0` es el USD₮0 de Arbitrum e HyperEVM — el `₮` se escribe `T` en el nombre del fichero (ver
 * `bajar-logos.mjs`), y aquí se hace la misma sustitución al buscar.
 */
const FAMILIAS = [
  ["ETH", "WETH"],
  ["BTC", "WBTC", "BTCB", "UBTC", "CBBTC"],
  ["BNB", "WBNB"],
  ["HYPE", "WHYPE"],
  ["USDT", "USDT0"],
  ["QQQ", "QQQB"],
  // Acciones tokenizadas B20 de Coinbase (Base): el token reporta el símbolo con sufijo "c"
  // (NVDAc, GOOGLc…), que normalizado es NVDAC/GOOGLC. Se mapean a su marca para heredar logo y color.
  ["NVDA", "NVDAC"],
  ["GOOGL", "GOOGLC"],
  ["META", "METAC"],
  ["MSFT", "MSFTC"],
  ["AMZN", "AMZNC"],
  ["TSLA", "TSLAC"],
  ["MSTR", "MSTRC"],
];

const normal = (sym: string) => (sym || "").toUpperCase().replace(/₮/g, "T").replace(/[^A-Z0-9]/g, "");

function logoDe(sym: string) {
  const u = normal(sym);
  if (LOGOS[u]) return LOGOS[u];
  const familia = FAMILIAS.find((f) => f.includes(u));
  return familia?.map((x) => LOGOS[x]).find(Boolean);
}

/** Insignia redonda con una letra, para la moneda que todavía no tiene fichero. */
function Punto({ bg, fg, t }: { bg: string; fg: string; t: string }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden focusable="false">
      <circle cx="16" cy="16" r="16" fill={bg} />
      <text
        x="16" y="21" fontFamily="Arial,Helvetica,sans-serif" fontSize="15" fontWeight="800"
        textAnchor="middle" fill={fg}
      >
        {t}
      </text>
    </svg>
  );
}

/** Los colores de marca de lo que se ha visto pasar por aquí, para que la insignia no sea gris. */
const COLORES: Record<string, [string, string]> = {
  USDG: ["#0B1F3A", "#3ECF8E"], USDT: ["#26A17B", "#fff"], USDC: ["#2775CA", "#fff"],
  ARB: ["#213147", "#12AAFF"], HYPE: ["#072723", "#97FCE4"], GME: ["#E11B22", "#fff"],
  NVDA: ["#76B900", "#fff"], TSLA: ["#E31937", "#fff"], MSFT: ["#00A4EF", "#fff"],
  AMZN: ["#232F3E", "#FF9900"], GOOGL: ["#1A73E8", "#fff"], SPY: ["#1B3A5B", "#fff"],
  META: ["#0866FF", "#fff"], MSTR: ["#F5821F", "#fff"],
  QQQ: ["#5A3E9B", "#fff"], AAPL: ["#111", "#fff"], ETH: ["#627EEA", "#fff"],
  BTC: ["#F7931A", "#fff"], CAKE: ["#1FC7D4", "#fff"],
};

/** El logo de una moneda. Si no la conocemos, su inicial sobre su color: nunca un hueco vacío. */
export function Moneda({ sym, size = 22 }: { sym: string; size?: number }) {
  const url = logoDe(sym);
  const u = normal(sym);
  const [bg, fg] = COLORES[u] ?? COLORES[FAMILIAS.find((f) => f.includes(u))?.[0] ?? ""] ?? ["#3a4a66", "#cbd5e1"];
  return (
    <span className="moneda" style={{ width: size, height: size }} title={sym}>
      {url
        ? <img src={url} alt="" width={size} height={size} loading="lazy" draggable={false} />
        : <Punto bg={bg} fg={fg} t={u[0] ?? "?"} />}
    </span>
  );
}

/**
 * El par, con las dos monedas montadas una sobre otra. Es la convención de todos los DEX y se lee
 * antes que el texto: quien busca su vault de NVDA lo encuentra por el verde, no leyendo cuatro
 * letras.
 */
export function ParMonedas({ s0, s1, size = 26 }: { s0: string; s1: string; size?: number }) {
  return (
    <span className="par-monedas" style={{ ["--m" as string]: `${size}px` }}>
      <Moneda sym={s0} size={size} />
      <Moneda sym={s1} size={size} />
    </span>
  );
}

/**
 * La marca del DEX donde vive la pool. NO es lo mismo que la red: en BNB Chain el mercado es de
 * PancakeSwap y en HyperEVM de PRJX, y enseñar el unicornio de Uniswap en las cuatro sería decir
 * que la pool está en un sitio donde no está (ver `enlaces.ts`).
 */
const DEX_DE_LA_RED: Record<string, string> = {
  arbitrum: "UNISWAP", robinhood: "UNISWAP", hyperevm: "PRJX", bnb: "PANCAKESWAP", base: "AERODROME",
};

/**
 * La marca que acompaña al NOMBRE de la red, que no siempre es la del DEX.
 *
 * En Robinhood Chain las pools son de Uniswap V3, pero la app llama a esa red «Robinhood ·
 * Acciones» — porque lo que la distingue es de quién son las acciones, no quién publica el mercado.
 * El unicornio junto a la palabra «Robinhood» se lee como un error, y con razón: el logo tiene que
 * ser el de la marca que está escrita al lado.
 */
const MARCA_DE_LA_RED: Record<string, string> = {
  arbitrum: "UNISWAP", robinhood: "ROBINHOOD", hyperevm: "PRJX", bnb: "PANCAKESWAP", base: "AERODROME",
};

function Marca({ nombre, size }: { nombre: string | undefined; size: number }) {
  const url = LOGOS_DEX[nombre ?? ""];
  if (!url) return null;
  return (
    <span className="moneda" style={{ width: size, height: size }}>
      <img src={url} alt="" width={size} height={size} loading="lazy" draggable={false} />
    </span>
  );
}

/** El fichero de logo de una marca de DEX por su nombre («UNISWAP», «PRJX»…), o undefined si no lo
 *  tenemos. Es el mismo bundle que usa `Marca`, expuesto para pintar la marca suelta —la portada
 *  enseña las cuatro flotando, sin red asociada. */
export function logoDex(nombre: string): string | undefined {
  return LOGOS_DEX[(nombre || "").toUpperCase()];
}

/** Para hablar de la POOL: «ver la pool en Uniswap» va con el logo de Uniswap, esté en la red que esté. */
export function IconoDex({ clave, size = 18 }: { clave: string; size?: number }) {
  return <Marca nombre={DEX_DE_LA_RED[clave]} size={size} />;
}

/** Para hablar de la RED: la que se elige en el selector y se nombra en la barra. */
export function IconoRed({ clave, size = 18 }: { clave: string; size?: number }) {
  return <Marca nombre={MARCA_DE_LA_RED[clave]} size={size} />;
}
